import React from 'react';
import { FileTextIcon, ShieldCheckIcon, ClockIcon } from 'lucide-react';
import { Seo } from '../components/ui/Seo';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CtaBanner } from '../components/ui/CtaBanner';
import { PROCESS_TIMELINE } from '../data/content';
import { IMAGES } from '../data/images';
import { useLanguage } from '../i18n/LanguageContext';
const REQUIREMENT_ICONS = [FileTextIcon, ShieldCheckIcon, ClockIcon];
export function HowItWorks() {
  const {
    lang,
    p
  } = useLanguage();
  const h = p.how;
  return <>
      <Seo title={h.seo.title} description={h.seo.description} data-id="element-473" data-mp="1ohvrmdfwb5wh" />
      <PageHero eyebrow={h.hero.eyebrow} title={h.hero.title} description={h.hero.description} breadcrumb={h.hero.breadcrumb} image={IMAGES.cta} data-id="element-474" data-mp="1tbgog25r5tu3" />

      <section className="w-full bg-white py-24 lg:py-32" data-id="element-475" data-mp="10t30qve4ro8q">
        <div className="mx-auto max-w-container px-6" data-id="element-476" data-mp="1rreww274vilj">
          <SectionHeading eyebrow={h.timelineEyebrow} title={h.timelineTitle} align="center" data-id="element-477" data-mp="1rr51u078el8z" />

          <ol className="relative mx-auto mt-20 max-w-4xl" data-id="element-478" data-mp="1nql7f4e1lvqj">
            <span className="absolute left-6 top-2 bottom-2 hidden w-px bg-navy-100 sm:block" aria-hidden="true" data-id="element-479" data-mp="1iytcyirrn5v4" />
            {PROCESS_TIMELINE.map((step, i) => <Reveal as="li" key={step.copy.en.title} delay={i * 0.05} data-id="element-480" data-mp="191fa5lyrmfla" data-mpi={i}>
                <div className="relative flex flex-col gap-6 pb-12 sm:flex-row" data-id="element-481" data-mp="1422h2jhawca9">
                  <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 bg-white text-gold-dark shadow-soft" data-id="element-482" data-mp="144f61glh7mwt">
                    <step.Icon className="h-5 w-5" aria-hidden="true" data-id="element-483" data-mp="v2ywtc6edqu6" />
                  </span>
                  <div className="rounded-2xl border border-navy-100 bg-white p-7 shadow-soft transition-all duration-300 hover:border-gold/40 hover:shadow-lift sm:ml-4" data-id="element-484" data-mp="2udzz007ex2ho">
                    <div className="flex flex-wrap items-center gap-4" data-id="element-485" data-mp="38js8rbastqzh">
                      <span className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-gold-dark" data-id="element-486" data-mp="3daib0fnxhdpy">
                        {h.stage} {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="rounded-full bg-navy-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-navy-700" data-id="element-487" data-mp="34hx2rthxapfd">
                        {step.duration[lang]}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-2xl font-medium text-navy" data-id="element-488" data-mp="3fhbzp8irdw">
                      {step.copy[lang].title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-charcoal-light" data-id="element-489" data-mp="3n854c75z57tg">
                      {step.copy[lang].description}
                    </p>
                  </div>
                </div>
              </Reveal>)}
          </ol>
        </div>
      </section>

      <section className="w-full bg-mist py-24 lg:py-32" data-id="element-490" data-mp="24a07c2gufxbp">
        <div className="mx-auto max-w-container px-6" data-id="element-491" data-mp="248fcebqmacdb">
          <SectionHeading eyebrow={h.expectationsEyebrow} title={h.expectationsTitle} align="center" data-id="element-492" data-mp="1zbynvgfi9vzp" />
          <ul className="mt-16 grid gap-6 lg:grid-cols-3" data-id="element-493" data-mp="2d8u9e8cy1fwn">
            {h.requirements.map((block, i) => {
            const Icon = REQUIREMENT_ICONS[i];
            return <Reveal as="li" key={block.title} delay={i * 0.06} data-id="element-494" data-mp="1kmsnvq8ug1qr" data-mpi={i}>
                  <div className="h-full rounded-2xl border border-navy-100 bg-white p-8 shadow-soft" data-id="element-495" data-mp="1glgzfo1phmk5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-gold" data-id="element-496" data-mp="1ud468ht9ydh6">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-497" data-mp="1pkkiis0vi1yn" />
                    </span>
                    <h3 className="mt-6 font-display text-xl font-semibold text-navy" data-id="element-498" data-mp="11ugq7gt4xomy">
                      {block.title}
                    </h3>
                    <ul className="mt-5 space-y-3" data-id="element-499" data-mp="wuu9gxe3lq9e">
                      {block.points.map(point => <li key={point} className="flex gap-3 text-sm leading-relaxed text-charcoal-light" data-id="element-500" data-mp="1fii869ceyvmz">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" data-id="element-501" data-mp="1kfqn8129mjk0" />
                          {point}
                        </li>)}
                    </ul>
                  </div>
                </Reveal>;
          })}
          </ul>
        </div>
      </section>

      <CtaBanner title={h.ctaTitle} description={h.ctaDescription} data-id="element-502" data-mp="286w7csf8ny04" />
    </>;
}